import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Bell, Check, CheckCircle, Megaphone, Kanban, GraduationCap, ChatCircle, ArrowsDownUp, Lightning } from "@phosphor-icons/react";

const CATEGORY_CONFIG = {
  briefing: { icon: Megaphone, color: "#1E3A8A", label: "Briefing" },
  pipeline: { icon: Kanban, color: "#8B5CF6", label: "Pipeline" },
  academy: { icon: GraduationCap, color: "#10B981", label: "Academy" },
  messaging: { icon: ChatCircle, color: "#06B6D4", label: "Message" },
  distribution: { icon: ArrowsDownUp, color: "#F59E0B", label: "Distribution" },
  admin: { icon: Lightning, color: "#C5A059", label: "Announcement" },
  general: { icon: Bell, color: "#6B7280", label: "Notification" },
};

function timeAgo(dateStr) {
  if (!dateStr) return "";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

export default function NotificationBell({ testIdPrefix = "" }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const ref = useRef(null);

  const pfx = testIdPrefix ? `${testIdPrefix}-` : "";

  const fetchCount = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications/unread-count");
      setUnreadCount(data.unread_count || 0);
    } catch (err) {
      console.warn("Failed to fetch notification count:", err?.message);
    }
  }, []);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const { data } = await api.get("/notifications?limit=20");
      setNotifications(data || []);
    } catch (err) {
      console.warn("Failed to fetch notifications:", err?.message);
      setError(true);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchCount();
    const interval = setInterval(fetchCount, 15000);
    return () => clearInterval(interval);
  }, [fetchCount]);

  useEffect(() => {
    if (open) fetchNotifications();
  }, [open, fetchNotifications]);

  // Close on click outside
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const markRead = async (id, e) => {
    if (e) { e.stopPropagation(); e.preventDefault(); }
    try {
      await api.post(`/notifications/${id}/read`);
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch (err) {
      console.warn("Failed to mark notification read:", err?.message);
    }
  };

  const markAllRead = async () => {
    try {
      await api.post("/notifications/mark-all-read");
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.warn("Failed to mark all read:", err?.message);
    }
  };

  const handleClick = (n) => {
    if (!n.read) markRead(n.id);
    if (n.link) navigate(n.link);
    setOpen(false);
  };

  return (
    <div className="relative" ref={ref} data-testid={`${pfx}notification-bell-wrapper`}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative p-2 rounded-lg hover:bg-[#F3F4F6] transition-colors"
        data-testid={`${pfx}notification-bell-btn`}
        aria-label="Notifications"
      >
        <Bell size={20} weight={open ? "duotone" : "regular"} className="text-[#0F172A]" />
        {unreadCount > 0 && (
          <span
            className="absolute -top-1 -right-1 bg-[#EF4444] text-white text-[9px] font-bold min-w-[18px] h-[18px] rounded-full flex items-center justify-center px-1 shadow-sm"
            data-testid={`${pfx}notification-unread-badge`}
          >
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-[360px] max-h-[480px] bg-white rounded-lg border border-[#E5E7EB] shadow-lg z-50 flex flex-col"
          data-testid={`${pfx}notification-dropdown`}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E7EB]">
            <h3 className="text-sm font-semibold text-[#0F172A]">Notifications</h3>
            {unreadCount > 0 && (
              <button
                onClick={markAllRead}
                className="text-[11px] text-[#1E3A8A] hover:underline font-medium flex items-center gap-1"
                data-testid={`${pfx}mark-all-read-btn`}
              >
                <CheckCircle size={14} /> Mark all read
              </button>
            )}
          </div>

          {/* List */}
          <div className="flex-1 overflow-y-auto" data-testid={`${pfx}notification-list`}>
            {loading ? (
              <div className="p-4 space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-14 skeleton-shimmer rounded-lg" />
                ))}
              </div>
            ) : error ? (
              <div className="py-12 text-center">
                <p className="text-sm text-[#EF4444] mb-2">Failed to load notifications</p>
                <button
                  onClick={fetchNotifications}
                  className="text-xs text-[#1E3A8A] hover:underline font-medium"
                  data-testid={`${pfx}notification-retry-btn`}
                >
                  Try again
                </button>
              </div>
            ) : notifications.length === 0 ? (
              <div className="py-12 text-center">
                <Bell size={32} className="mx-auto text-[#D1D5DB] mb-2" />
                <p className="text-sm text-[#6B7280]">No notifications yet</p>
              </div>
            ) : (
              notifications.map((n) => {
                const cat = CATEGORY_CONFIG[n.category] || CATEGORY_CONFIG.general;
                const Icon = cat.icon;
                return (
                  <div
                    key={n.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => handleClick(n)}
                    onKeyDown={(e) => e.key === "Enter" && handleClick(n)}
                    className={`w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-[#F9FAFB] transition-colors border-b border-[#F3F4F6] last:border-0 cursor-pointer ${
                      !n.read ? "bg-[#F0F4FF]" : ""
                    }`}
                    data-testid={`notification-item-${n.id}`}
                  >
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                      style={{ backgroundColor: `${cat.color}15` }}
                    >
                      <Icon size={16} weight="duotone" style={{ color: cat.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className={`text-xs font-semibold truncate ${!n.read ? "text-[#0F172A]" : "text-[#6B7280]"}`}>
                          {n.title}
                        </p>
                        {!n.read && <div className="w-1.5 h-1.5 rounded-full bg-[#1E3A8A] flex-shrink-0" />}
                      </div>
                      <p className="text-xs text-[#6B7280] truncate mt-0.5">{n.body}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-[10px] text-[#9CA3AF]">{timeAgo(n.created_at)}</span>
                        <span
                          className="text-[10px] px-1.5 py-0.5 rounded font-medium"
                          style={{ backgroundColor: `${cat.color}18`, color: cat.color }}
                        >
                          {cat.label}
                        </span>
                      </div>
                    </div>
                    {!n.read && (
                      <button
                        onClick={(e) => markRead(n.id, e)}
                        className="p-1 hover:bg-[#E5E7EB] rounded flex-shrink-0"
                        title="Mark as read"
                        data-testid={`mark-read-${n.id}`}
                      >
                        <Check size={14} className="text-[#6B7280]" />
                      </button>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
